import {
    ActiveFace,
    alignToNearerEdge,
    along,
    CabinetFace,
    facesFrontOrBack,
    getActiveFaceFromCamera,
    getBoxDistance,
    getDirection,
    maxAlong,
    minAlong,
    PlanAxis,
} from 'components/customer/RoomPlanner/helpers/calculations';
import {
    PairFacts,
    SnapState,
    SnapStrategy,
} from 'components/customer/RoomPlanner/helpers/snapping/types';
import {CabinetMesh} from 'components/customer/RoomPlanner/types';
import {
    Box3,
    BufferGeometry,
    Group,
    Material,
    Mesh,
    NormalBufferAttributes,
    Object3D,
    Object3DEventMap,
    Vector3,
} from 'three';
import {calculateBestSnapped} from 'components/customer/RoomPlanner/helpers/useHandleSnapping';
import {checkCabinetOverlap} from 'components/customer/RoomPlanner/helpers/overlap';
import {toCandidate} from 'components/customer/RoomPlanner/helpers/snapping/strategies/candidate';
import {FILLER_SNAPPING_DISTANCE} from 'components/customer/RoomPlanner/helpers/snapping/constants';

// Box measurements and face lookups only the facing snap uses.

enum Axis {
    X = 'x',
    Z = 'z',
}

const getBoxWidth = (a: Box3): number => a.max.x - a.min.x;
const getBoxDepth = (a: Box3): number => a.max.z - a.min.z;

const getCenterZ = (a: Box3): number => (a.min.z + a.max.z) / 2;
const getCenterX = (a: Box3): number => (a.min.x + a.max.x) / 2;

/**
 * How near the two boxes' edges come along an axis: the smallest distance
 * between an edge of one and an edge of the other, both edges of each
 * compared.
 * @param {PlanAxis} axis The axis to measure along.
 * @param {Box3} a The dragged box.
 * @param {Box3} b The other box.
 * @return {number} The smallest edge-to-edge distance.
 */
export const edgeDistance = (axis: PlanAxis, a: Box3, b: Box3): number => {
    const [aMin, aMax] = [minAlong(a, axis), maxAlong(a, axis)];
    const [bMin, bMax] = [minAlong(b, axis), maxAlong(b, axis)];

    return Math.min(
        Math.abs(aMin - bMin),
        Math.abs(aMin - bMax),
        Math.abs(aMax - bMin),
        Math.abs(aMax - bMax)
    );
};

type CabinetDirection = 'normal' | 'right' | 'back' | 'left';

const getIsFacingBackNormal = (
    otherCabinetDirection: CabinetDirection,
    activeFace: ActiveFace
): boolean =>
    (otherCabinetDirection === 'normal' && activeFace.frontBack.z === -1) ||
    (otherCabinetDirection === 'back' && activeFace.frontBack.z === 1);

const getIsFacingLeftRightSide = (
    otherCabinetDirection: CabinetDirection,
    activeFace: ActiveFace
): boolean =>
    (otherCabinetDirection === 'left' && activeFace.leftRight.x === 1) ||
    (otherCabinetDirection === 'right' && activeFace.leftRight.x === -1);

const getBoxFaces = (box: Box3): CabinetFace[] => [
    {normal: new Vector3(-1, 0, 0), position: box.min.x},
    {normal: new Vector3(1, 0, 0), position: box.max.x},
    {normal: new Vector3(0, 0, -1), position: box.min.z},
    {normal: new Vector3(0, 0, 1), position: box.max.z},
];

/**
 * Adjusts the shifted bounding box of an L-shaped filler product based on
 * the relative positions and rotation direction of the cabinet group.
 *
 * @param {Group<Object3DEventMap>} cabinetGroup - The Three.js group representing the cabinet.
 * @param {number} otherCenterX - The center X position of the other (adjacent) cabinet.
 * @param {number} selectedCenterX - The center X position of the currently selected cabinet.
 * @param {Box3} shiftedBox - The bounding box to adjust in place.
 */
function useOtherProductFillerProductAdjustLShape(
    cabinetGroup: Group<Object3DEventMap>,
    otherCenterX: number,
    selectedCenterX: number,
    shiftedBox: Box3
) {
    if (
        getDirection(cabinetGroup.rotation.z) == 'normal' &&
        otherCenterX > selectedCenterX
    ) {
        shiftedBox.max.z +=
            (cabinetGroup as CabinetMesh).userData.values
                .cabinet_ext_thickness * 0.2;
    }

    if (
        getDirection(cabinetGroup.rotation.z) == 'back' &&
        selectedCenterX > otherCenterX
    ) {
        shiftedBox.min.z -=
            (cabinetGroup as CabinetMesh).userData.values
                .cabinet_ext_thickness * 0.2;
    }
}

/**
 * Finds the mesh within a group whose center is closest to a given face,
 * measured by projection onto the face normal.
 *
 * @param {Group<Object3DEventMap>} group - The Three.js group to search within.
 * @param {Vector3} faceNormal - The normal vector of the face to project against.
 * @return {Mesh | null} The closest {@link Mesh} to the face, or `null` if the group contains no meshes.
 */
function getClosestMeshToFace(
    group: Group<Object3DEventMap>,
    faceNormal: Vector3
): Mesh | null {
    let closestMesh: Mesh | null = null;
    let bestDistance = Infinity;

    group.updateWorldMatrix(true, true);

    group.traverse((child: Object3D) => {
        if (!(child instanceof Mesh)) return;

        const box = new Box3().setFromObject(child);
        // An empty box's centre is the world origin, which would otherwise
        // win depending on where the product sits in the room.
        if (box.isEmpty()) return;

        const center = new Vector3();
        box.getCenter(center);

        const distance = center.dot(faceNormal);

        if (distance < bestDistance) {
            bestDistance = distance;
            closestMesh = child as Mesh<
                BufferGeometry<NormalBufferAttributes>,
                Material | Material[],
                Object3DEventMap
            >;
        }
    });

    return closestMesh;
}

const getOtherBoxToClosestMesh = (
    otherBox: Box3,
    otherProduct: Group<Object3DEventMap>,
    faceDir: Vector3,
    axis: Axis,
    isProductFillerProduct: boolean
): void => {
    const perpAxis = axis === Axis.X ? Axis.Z : Axis.X;
    const faceComponent = perpAxis === Axis.X ? faceDir.x : faceDir.z;

    const faceNormal = new Vector3(
        axis === Axis.Z ? faceComponent * -1 : 0,
        0,
        axis === Axis.X ? faceComponent * -1 : 0
    );

    const closestMesh = getClosestMeshToFace(otherProduct, faceNormal);

    if (closestMesh) {
        const newBox = new Box3().setFromObject(closestMesh);

        if (isProductFillerProduct) {
            if (getDirection(otherProduct.rotation.z) == 'normal') {
                newBox.max.z +=
                    (otherProduct as CabinetMesh).userData.values
                        .cabinet_ext_thickness * 0.2;
            }
            if (getDirection(otherProduct.rotation.z) == 'back') {
                newBox.min.z -=
                    (otherProduct as CabinetMesh).userData.values
                        .cabinet_ext_thickness * 0.2;
            }
        }

        if (axis === Axis.X) {
            otherBox.min.x = newBox.min.x;
            otherBox.max.x = newBox.max.x;
        } else {
            otherBox.min.z = newBox.min.z;
            otherBox.max.z = newBox.max.z;
        }
    }
};

const getShiftedBoxForClosetFace = (
    cabinetGroup: Group<Object3DEventMap>,
    faceNormal: Vector3,
    delta: Vector3
): Box3 => {
    const closestMesh = getClosestMeshToFace(cabinetGroup, faceNormal);
    return new Box3().setFromObject(closestMesh).clone().translate(delta);
};

type SnapAxis = 'x' | 'z';

interface SnapPositionParams {
    axis: SnapAxis;
    selectedCenterX: number;
    selectedCenterZ: number;
    otherCenterX: number;
    otherCenterZ: number;
    selectedCabinetDirection: string;
    isFrontFaceSnappingCabinetProduct: boolean;
    isOtherProductFillerProduct: boolean;
    isCabinetProductFiller: boolean;
    selectedBoxWidth: number;
    selectedBoxDepth: number;
    otherBoxDepth: number;
    shiftedBox: Box3;
    otherBox: Box3;
    cabinetGroup: Group;
    otherProduct: CabinetMesh;
    delta: Vector3;
}

/**
 * Resolves the snap position for a cabinet relative to another product
 * based on axis alignment, cabinet orientation, filler conditions,
 * and bounding box dimensions.
 *
 * @param {SnapPositionParams} params - Parameters used to calculate snapping.
 * @param {SnapAxis} params.axis - The axis being evaluated for snapping.
 * @param {number} params.selectedCenterX - X center of the selected cabinet.
 * @param {number} params.selectedCenterZ - Z center of the selected cabinet.
 * @param {number} params.otherCenterX - X center of the other product.
 * @param {number} params.otherCenterZ - Z center of the other product.
 * @param {string} params.selectedCabinetDirection - Direction the selected cabinet is facing.
 * @param {boolean} params.isFrontFaceSnappingCabinetProduct - Whether front-face snapping is enabled.
 * @param {boolean} params.isOtherProductFillerProduct - Whether the other product is a filler.
 * @param {boolean} params.isCabinetProductFiller - Whether the selected cabinet is a filler.
 * @param {number} params.selectedBoxWidth - Width of the selected cabinet box.
 * @param {number} params.selectedBoxDepth - Depth of the selected cabinet box.
 * @param {number} params.otherBoxDepth - Depth of the other product box.
 * @param {Box3} params.shiftedBox - Current shifted bounding box of the selected cabinet.
 * @param {Box3} params.otherBox - Bounding box of the other product.
 * @param {Group} params.cabinetGroup - Cabinet group associated with the selected cabinet.
 * @param {CabinetMesh} params.otherProduct - The product being snapped against.
 * @param {Vector3} params.delta - Snap threshold or positional tolerance.
 * @return {{snappedPrimary: (number|null), shiftedBox: Box3}} The resolved snapped position and updated bounding box.
 */
function resolveCabinetSnapPosition({
    axis,
    selectedCenterX,
    selectedCenterZ,
    otherCenterX,
    otherCenterZ,
    selectedCabinetDirection,
    isFrontFaceSnappingCabinetProduct,
    isOtherProductFillerProduct,
    isCabinetProductFiller,
    selectedBoxWidth,
    selectedBoxDepth,
    otherBoxDepth,
    shiftedBox,
    otherBox,
    cabinetGroup,
    otherProduct,
    delta,
}: SnapPositionParams): {
    snappedPrimary: number | null;
    shiftedBox: Box3;
} {
    if (isCabinetProductFiller) {
        return {snappedPrimary: null, shiftedBox};
    }

    let snappedPrimary: number | null = null;

    const isXAxis = axis === 'x';

    const selectedCenter = isXAxis ? selectedCenterX : selectedCenterZ;
    const otherCenter = isXAxis ? otherCenterX : otherCenterZ;

    const multiplier = selectedCenter < otherCenter ? -1 : 1;

    const size = isXAxis ? getBoxWidth(shiftedBox) : getBoxDepth(shiftedBox);

    if (isXAxis) {
        if (selectedCabinetDirection === 'left') {
            if (selectedCenter < otherCenter) {
                snappedPrimary = otherBox.max.x + multiplier * size;

                if (isOtherProductFillerProduct) {
                    snappedPrimary =
                        otherBox.min.x +
                        multiplier * selectedBoxWidth -
                        multiplier *
                            otherProduct.userData.values.cabinet_ext_thickness *
                            0.2;
                }
            }

            if (
                selectedCenter > otherCenter &&
                isFrontFaceSnappingCabinetProduct
            ) {
                shiftedBox = getShiftedBoxForClosetFace(
                    cabinetGroup,
                    new Vector3(1, 0, 0),
                    delta
                );

                snappedPrimary =
                    otherBox.max.x + multiplier * getBoxWidth(shiftedBox);
            }
        } else if (selectedCabinetDirection === 'right') {
            if (selectedCenter > otherCenter) {
                snappedPrimary = otherBox.min.x + multiplier * size;
            }

            if (
                selectedCenter < otherCenter &&
                isFrontFaceSnappingCabinetProduct
            ) {
                shiftedBox = getShiftedBoxForClosetFace(
                    cabinetGroup,
                    new Vector3(-1, 0, 0),
                    delta
                );

                snappedPrimary =
                    otherBox.min.x + multiplier * getBoxWidth(shiftedBox);
            }
        }
    } else {
        if (selectedCabinetDirection === 'normal') {
            if (selectedCenter > otherCenter) {
                snappedPrimary = otherBox.min.z + multiplier * size;

                if (isOtherProductFillerProduct) {
                    snappedPrimary =
                        otherBox.min.z +
                        multiplier * selectedBoxDepth +
                        multiplier * otherBoxDepth -
                        multiplier *
                            otherProduct.userData.values.cabinet_ext_thickness *
                            0.2;
                }
            }

            if (
                selectedCenter < otherCenter &&
                isFrontFaceSnappingCabinetProduct
            ) {
                shiftedBox = getShiftedBoxForClosetFace(
                    cabinetGroup,
                    new Vector3(0, 0, -1),
                    delta
                );

                snappedPrimary =
                    otherBox.min.z + multiplier * getBoxDepth(shiftedBox);
            }
        } else if (selectedCabinetDirection === 'back') {
            if (selectedCenter < otherCenter) {
                snappedPrimary = otherBox.max.z + multiplier * size;
            }

            if (
                selectedCenter > otherCenter &&
                isFrontFaceSnappingCabinetProduct
            ) {
                shiftedBox = getShiftedBoxForClosetFace(
                    cabinetGroup,
                    new Vector3(0, 0, 1),
                    delta
                );

                snappedPrimary =
                    otherBox.max.z + multiplier * getBoxDepth(shiftedBox);
            }
        }
    }

    return {
        snappedPrimary,
        shiftedBox,
    };
}

export const resolveSnappedY = (
    shiftedBox: Box3,
    otherBox: Box3,
    targetPos: Vector3,
    cabinetGroup: Group<Object3DEventMap>,
    otherProduct: Group<Object3DEventMap>,
    isCabinetProductFiller: boolean,
    isUpperPanel: boolean,
    snapState: SnapState
): number => {
    const otherHeight = otherBox.max.y - otherBox.min.y;
    const selectedHeight = shiftedBox.max.y - shiftedBox.min.y;

    if (!isCabinetProductFiller) {
        return targetPos.y;
    }

    if (selectedHeight >= otherHeight) {
        if ((otherProduct as CabinetMesh).userData.isUpperProduct) {
            return cabinetGroup.position.y;
        }

        // A filler at least as tall as the other product isn't held in
        // snap mode against it.
        snapState.mode = 'detached';
        return cabinetGroup.position.y;
    }

    let legBox: Box3 = null;
    otherProduct.traverse((child: Object3D) => {
        if (child instanceof Mesh) {
            if (child.name.toLowerCase().includes('adjustable_leg')) {
                legBox = new Box3().setFromObject(child);
            }
        }
    });

    let minY = otherBox.min.y;

    if (legBox) {
        minY = legBox.max.y;
    }

    if (isCabinetProductFiller) {
        if (selectedHeight < otherHeight) {
            const SnapDistance = isUpperPanel ? 25 : 50;

            const distanceToTop = Math.abs(shiftedBox.max.y - otherBox.max.y);
            const distanceToBottom = Math.abs(shiftedBox.min.y - minY);
            // Snap to closest edge if within range
            if (
                distanceToTop < SnapDistance ||
                distanceToBottom < SnapDistance
            ) {
                if (distanceToTop < distanceToBottom) {
                    // Snap to top
                    return targetPos.y + (otherBox.max.y - shiftedBox.max.y);
                } else {
                    // Snap to bottom
                    return targetPos.y + (minY - shiftedBox.min.y);
                }
            }

            return targetPos.y;
        }
    }

    return targetPos.y;
};

/**
 * Where the drag target goes along an axis when the dragged box snaps beside
 * the other. A box at least as wide as the other lines up with its nearer
 * edge; a narrower one sits flush inside whichever end of the other the drag
 * target is nearer to.
 * @param {PlanAxis} axis The axis to place along.
 * @param {Box3} shiftedBox The dragged box at the drag target.
 * @param {Box3} otherBox The box it snaps beside.
 * @param {Vector3} targetPos The drag target.
 * @return {number} The snapped target along the axis.
 */
export const resolveSnappedAlong = (
    axis: PlanAxis,
    shiftedBox: Box3,
    otherBox: Box3,
    targetPos: Vector3
): number => {
    const selectedSize =
        maxAlong(shiftedBox, axis) - minAlong(shiftedBox, axis);
    const otherSize = maxAlong(otherBox, axis) - minAlong(otherBox, axis);

    if (selectedSize >= otherSize) {
        return alignToNearerEdge(axis, shiftedBox, otherBox, targetPos);
    }

    const target = along(targetPos, axis);
    const snappedMin =
        Math.abs(target - minAlong(otherBox, axis)) <=
        Math.abs(target - maxAlong(otherBox, axis))
            ? minAlong(otherBox, axis)
            : maxAlong(otherBox, axis) - selectedSize;

    return target + (snappedMin - minAlong(shiftedBox, axis));
};

// The facing snap's position, recording the snap state it would put on the
// dragged product into `snapState` rather than onto the product.
const findFacingSnap = (
    cabinetGroup: Group<Object3DEventMap>,
    otherProduct: Group<Object3DEventMap>,
    pair: PairFacts,
    targetPos: Vector3,
    currentPos: Vector3,
    activeFace: ActiveFace,
    snapState: SnapState
): Vector3 | null => {
    //  Common
    const delta = targetPos.clone().sub(currentPos);
    let legBox: Box3 = null;

    // Other Product. The box is a copy because the return allowance widens
    // it in place.
    const otherBox = pair.other.box.clone();
    const otherFaces = getBoxFaces(otherBox);
    const otherCabinetDirection = pair.other.direction;
    const otherBoxDepth = getBoxDepth(otherBox);
    const otherCenterZ = getCenterZ(otherBox);
    const otherCenterX = getCenterX(otherBox);
    const isOtherUpperProduct = pair.other.isUpper;

    const isReturnProduct = pair.other.isReturn || pair.other.isRightReturn;
    const otherCabinetHeight = (otherProduct as CabinetMesh).userData.values
        .cabinet_height;
    const isOtherLeftOrRight =
        otherCabinetDirection === 'left' || otherCabinetDirection === 'right';
    const isOtherNormalOrBack = facesFrontOrBack(otherCabinetDirection);
    const isFacingBackNormalOtherCabinet = getIsFacingBackNormal(
        otherCabinetDirection,
        activeFace
    );
    const isFacingLeftRightSideOtherCabinet = getIsFacingLeftRightSide(
        otherCabinetDirection,
        activeFace
    );

    otherProduct.traverse((child: Object3D) => {
        if (child instanceof Mesh) {
            if (child.name.toLowerCase().includes('adjustable_leg')) {
                legBox = new Box3().setFromObject(child);
            }
        }
    });

    const isOtherProductFillerProduct =
        (otherProduct as CabinetMesh).userData.isFillerProduct ||
        (otherProduct as CabinetMesh).userData.isVerminProduct;

    const isOtherProductFillerPanel =
        (otherProduct as CabinetMesh).userData.isManualPanel ||
        (otherProduct as CabinetMesh).userData.isPanelProduct;

    const isOtherProductFiller = pair.other.isFiller;

    const {isReturn: isOtherReturn, isRightReturn: isOtherRightReturn} =
        pair.other;

    const isFrontFaceSnappingOtherProduct = (otherProduct as CabinetMesh)
        .userData.isLShapedProduct;

    //  Selected Product
    // A copy, as for the other product: the dragged product's box is shared
    // by every pair in the drag.
    const selectedBox = pair.selected.box.clone();
    let shiftedBox = selectedBox.clone().translate(delta);
    const selectedFaces = getBoxFaces(shiftedBox);
    const selectedCabinetDirection = pair.selected.direction;
    const selectedBoxWidth = getBoxWidth(selectedBox);
    const selectedBoxDepth = getBoxDepth(selectedBox);
    const selectedCenterZ = getCenterZ(selectedBox);
    const selectedCenterX = getCenterX(selectedBox);
    const dist = getBoxDistance(shiftedBox, otherBox);
    const isProductFillerProduct =
        (cabinetGroup as CabinetMesh).userData.isFillerProduct ||
        (cabinetGroup as CabinetMesh).userData.isVerminProduct;
    const isCabinetProductFiller = pair.selected.isFiller;
    const isUpperProduct = pair.selected.isUpper;
    const isUpperPanel = isUpperProduct && isCabinetProductFiller;
    const selectedCabinetHeight = (cabinetGroup as CabinetMesh).userData.values
        .cabinet_height;
    const isSelectedCabinetLeftOrRight =
        selectedCabinetDirection === 'left' ||
        selectedCabinetDirection === 'right';
    const isSelectedCabinetNormalOrBack = facesFrontOrBack(
        selectedCabinetDirection
    );
    const isFrontFaceSnappingCabinetProduct = (cabinetGroup as CabinetMesh)
        .userData.isLShapedProduct;

    const isLShapePanelSnappingNormalBackOther =
        (otherCabinetDirection == 'normal' && selectedCenterX > otherCenterX) ||
        (otherCabinetDirection == 'back' && selectedCenterX < otherCenterX);

    const isLShapePanelSnappingLeftRightOther =
        (otherCabinetDirection == 'left' && selectedCenterZ > otherCenterZ) ||
        (otherCabinetDirection == 'right' && selectedCenterZ < otherCenterZ);

    const isLShapePanelSnappingNormalBackCabinet =
        (selectedCabinetDirection == 'normal' &&
            selectedCenterX < otherCenterX) ||
        (selectedCabinetDirection == 'back' && selectedCenterX > otherCenterX);

    const isLShapePanelSnappingLeftRightCabient =
        (selectedCabinetDirection == 'left' &&
            selectedCenterZ < otherCenterZ) ||
        (selectedCabinetDirection == 'right' && selectedCenterZ > otherCenterZ);

    const isOtherTallCabinetSnappableToUpper =
        Math.abs(selectedBox.max.y - otherBox.max.y) < 5 &&
        isUpperProduct &&
        !isOtherUpperProduct;

    const extraDepth = 2 * 0.2;

    //  Common
    let bestDistance = Infinity;
    let bestSnappedX: number | null = null;
    let bestSnappedZ: number | null = null;
    let bestSnappedY: number | null = null;

    let _snappedY: number | undefined;
    const getSnappedY = (): number => {
        if (_snappedY === undefined) {
            _snappedY = resolveSnappedY(
                shiftedBox,
                otherBox,
                targetPos,
                cabinetGroup,
                otherProduct,
                isCabinetProductFiller,
                isUpperProduct,
                snapState
            );
        }
        return _snappedY;
    };

    const selectedLeft = selectedFaces[0].position;
    const selectedRight = selectedFaces[1].position;
    const selectedBack = selectedFaces[2].position;
    const selectedFront = selectedFaces[3].position;

    const otherLeft = otherFaces[0].position;
    const otherRight = otherFaces[1].position;
    const otherBack = otherFaces[2].position;
    const otherFront = otherFaces[3].position;

    if (
        (isOtherReturn || isOtherRightReturn) &&
        !isCabinetProductFiller &&
        !isUpperProduct &&
        dist.x < FILLER_SNAPPING_DISTANCE &&
        dist.z < FILLER_SNAPPING_DISTANCE
    ) {
        switch (otherCabinetDirection) {
            case 'normal':
                if (
                    (selectedCabinetDirection == 'right' ||
                        selectedCabinetDirection == 'left') &&
                    selectedBack - otherFront < 10
                ) {
                    bestSnappedX = isOtherRightReturn
                        ? targetPos.x + (otherBox.max.x - shiftedBox.max.x)
                        : targetPos.x + (otherBox.min.x - shiftedBox.min.x);
                    bestSnappedZ =
                        targetPos.z +
                        (otherBox.max.z - shiftedBox.max.z) +
                        selectedBoxDepth;
                }
                break;

            case 'back':
                if (
                    (selectedCabinetDirection == 'left' ||
                        selectedCabinetDirection == 'right') &&
                    selectedFront - otherBack < 10
                ) {
                    bestSnappedX = isOtherRightReturn
                        ? targetPos.x + (otherBox.min.x - shiftedBox.min.x)
                        : targetPos.x + (otherBox.max.x - shiftedBox.max.x);
                    bestSnappedZ =
                        targetPos.z +
                        (otherBox.max.z - shiftedBox.max.z) -
                        selectedBoxWidth;
                }

                break;

            case 'left':
                if (
                    (selectedCabinetDirection == 'normal' ||
                        selectedCabinetDirection == 'back') &&
                    selectedRight - otherLeft < 10
                ) {
                    bestSnappedX =
                        targetPos.x +
                        (otherBox.min.x - shiftedBox.min.x) -
                        selectedBoxWidth;
                    bestSnappedZ = isOtherRightReturn
                        ? targetPos.z +
                          (otherBox.max.z - shiftedBox.min.z) -
                          selectedBoxDepth
                        : targetPos.z +
                          (otherBox.max.z - shiftedBox.min.z) -
                          otherBoxDepth;
                }

                break;

            case 'right':
                if (
                    (selectedCabinetDirection == 'back' ||
                        selectedCabinetDirection == 'normal') &&
                    selectedLeft - otherRight < FILLER_SNAPPING_DISTANCE
                ) {
                    bestSnappedX =
                        targetPos.x + (otherBox.max.x - shiftedBox.min.x);

                    bestSnappedZ = isOtherRightReturn
                        ? targetPos.z + (otherBox.min.z - shiftedBox.min.z)
                        : targetPos.z + (otherBox.max.z - shiftedBox.max.z);
                }

                break;
        }
        if (bestSnappedX === null || bestSnappedZ === null) {
            return null;
        }
        bestSnappedY = getSnappedY();

        return new Vector3(
            bestSnappedX ?? targetPos.x,
            bestSnappedY,
            bestSnappedZ ?? targetPos.z
        );
    }

    if (isUpperPanel) {
        if (!isOtherUpperProduct) {
            if (
                selectedCabinetDirection == 'normal' ||
                selectedCabinetDirection == 'back'
            ) {
                if (cabinetGroup.position.z < 0) {
                    if (Math.abs(selectedBox.min.z - otherBox.min.z) > 5) {
                        return null;
                    }
                }
                if (cabinetGroup.position.z > 0) {
                    if (Math.abs(selectedBox.max.z - otherBox.max.z) > 5) {
                        return null;
                    }
                }
            }

            if (
                selectedCabinetDirection == 'left' ||
                selectedCabinetDirection == 'right'
            ) {
                if (cabinetGroup.position.x < 0) {
                    if (Math.abs(selectedBox.min.x - otherBox.min.x) > 5) {
                        return null;
                    }
                }
                if (cabinetGroup.position.x > 0) {
                    if (Math.abs(selectedBox.max.x - otherBox.max.x) > 5) {
                        return null;
                    }
                }
            }
        }
    }

    for (const sFace of selectedFaces) {
        for (const oFace of otherFaces) {
            {
                const zDist = edgeDistance('z', shiftedBox, otherBox);
                const xDist = edgeDistance('x', shiftedBox, otherBox);

                const isFar =
                    dist.x >= FILLER_SNAPPING_DISTANCE ||
                    dist.z >= FILLER_SNAPPING_DISTANCE;
                const isNotBetter =
                    xDist >= bestDistance && zDist >= bestDistance;

                if (isFar || isNotBetter) continue;

                if (isOtherProductFiller && isCabinetProductFiller) {
                    if (isOtherLeftOrRight) {
                        if (
                            oFace.normal.x != activeFace.leftRight.x ||
                            oFace.normal.x == sFace.normal.x ||
                            sFace.normal.x == 0
                        ) {
                            continue;
                        }

                        bestDistance = xDist;
                        bestSnappedX =
                            targetPos.x + (oFace.position - sFace.position);

                        bestSnappedZ = resolveSnappedAlong(
                            'z',
                            shiftedBox,
                            otherBox,
                            targetPos
                        );
                        bestSnappedY = getSnappedY();
                    } else {
                        if (
                            oFace.normal.z != activeFace.frontBack.z ||
                            oFace.normal.z == sFace.normal.z ||
                            sFace.normal.z == 0
                        ) {
                            continue;
                        }

                        bestDistance = xDist;
                        bestSnappedZ =
                            targetPos.z + (oFace.position - sFace.position);

                        bestSnappedX = resolveSnappedAlong(
                            'x',
                            shiftedBox,
                            otherBox,
                            targetPos
                        );
                    }
                    continue;
                }
                if (isOtherNormalOrBack) {
                    if (isSelectedCabinetLeftOrRight) {
                        if (
                            oFace.normal.z != activeFace.frontBack.z ||
                            oFace.normal.z == sFace.normal.z ||
                            sFace.normal.z == 0
                        ) {
                            continue;
                        }

                        if (
                            isFrontFaceSnappingOtherProduct &&
                            !isFacingBackNormalOtherCabinet
                        ) {
                            getOtherBoxToClosestMesh(
                                otherBox,
                                otherProduct,
                                activeFace.frontBack,
                                Axis.X,
                                isProductFillerProduct
                            );
                        }

                        if (isReturnProduct) {
                            const voidWidth =
                                (otherProduct as CabinetMesh).userData.values
                                    .void_width * 0.2;

                            if (otherCabinetDirection == 'normal') {
                                if (
                                    (otherProduct as CabinetMesh).userData
                                        .isRightReturnProduct
                                ) {
                                    otherBox.max.x -= 1;
                                } else {
                                    otherBox.min.x += 1;
                                }
                            } else {
                                if (
                                    (otherProduct as CabinetMesh).userData
                                        .isRightReturnProduct
                                ) {
                                    otherBox.max.x += voidWidth;
                                } else {
                                    otherBox.min.x -= voidWidth;
                                }
                            }
                        }

                        if (
                            isCabinetProductFiller &&
                            (isFacingBackNormalOtherCabinet ||
                                isFrontFaceSnappingOtherProduct)
                        ) {
                            bestDistance = xDist;
                            bestSnappedZ =
                                targetPos.z + (oFace.position - sFace.position);

                            bestSnappedX = resolveSnappedAlong(
                                'x',
                                shiftedBox,
                                otherBox,
                                targetPos
                            );
                        }
                        if (!isCabinetProductFiller) {
                            const xSnapResult = resolveCabinetSnapPosition({
                                axis: 'x',
                                selectedCenterX,
                                selectedCenterZ,
                                otherCenterX,
                                otherCenterZ,
                                selectedCabinetDirection,
                                isFrontFaceSnappingCabinetProduct,
                                isOtherProductFillerProduct,
                                isCabinetProductFiller,
                                selectedBoxWidth,
                                selectedBoxDepth,
                                otherBoxDepth,
                                shiftedBox,
                                otherBox,
                                cabinetGroup,
                                otherProduct: otherProduct as CabinetMesh,
                                delta,
                            });

                            shiftedBox = xSnapResult.shiftedBox;
                            bestSnappedX = xSnapResult.snappedPrimary;

                            if (bestSnappedX == null) {
                                continue;
                            }

                            bestDistance = xDist;

                            bestSnappedZ = resolveSnappedAlong(
                                'z',
                                shiftedBox,
                                otherBox,
                                targetPos
                            );

                            bestSnappedX +=
                                selectedCabinetDirection == 'left'
                                    ? extraDepth
                                    : -extraDepth;
                        }
                    } else if (isSelectedCabinetNormalOrBack) {
                        if (
                            oFace.normal.x == sFace.normal.x ||
                            sFace.normal.z == 1 ||
                            sFace.normal.z == -1
                        ) {
                            continue;
                        }

                        const LShapeException =
                            (isFrontFaceSnappingOtherProduct &&
                                !isLShapePanelSnappingNormalBackOther) ||
                            (isProductFillerProduct &&
                                !isFrontFaceSnappingOtherProduct) ||
                            isOtherProductFillerProduct;

                        if (!LShapeException) {
                            getOtherBoxToClosestMesh(
                                otherBox,
                                otherProduct,
                                activeFace.leftRight,
                                Axis.Z,
                                isProductFillerProduct
                            );
                        }

                        if (
                            (isFrontFaceSnappingCabinetProduct &&
                                isLShapePanelSnappingNormalBackCabinet) ||
                            (isOtherProductFillerPanel &&
                                !isFrontFaceSnappingCabinetProduct)
                        ) {
                            const multiplier =
                                selectedCabinetDirection == 'back' ? 1 : -1;
                            shiftedBox = getShiftedBoxForClosetFace(
                                cabinetGroup,
                                new Vector3(multiplier * 1, 0, 0),
                                delta
                            );
                            if (isOtherProductFillerProduct) {
                                useOtherProductFillerProductAdjustLShape(
                                    cabinetGroup,
                                    otherCenterX,
                                    selectedCenterX,
                                    shiftedBox
                                );
                            }
                        }

                        const snappedX = calculateBestSnapped({
                            isCabinetProductFiller,
                            oFace,
                            sFace,
                            activeFace,
                            targetPos,
                            otherCenterAlong: otherCenterX,
                            selectedCenterAlong: selectedCenterX,
                            selectedCabinetDirection,
                            otherBox,
                            selectedSizeAlong: selectedBoxWidth,
                            axis: 'x',
                            selectedCenterAcross: selectedCenterZ,
                        });

                        if (snappedX === null) {
                            continue;
                        }

                        bestSnappedX = snappedX;

                        bestDistance = zDist;
                        bestSnappedZ = resolveSnappedAlong(
                            'z',
                            shiftedBox,
                            otherBox,
                            targetPos
                        );
                    }
                }
                if (isOtherLeftOrRight) {
                    if (isSelectedCabinetNormalOrBack) {
                        if (
                            oFace.normal.x != activeFace.leftRight.x ||
                            oFace.normal.x == sFace.normal.x ||
                            sFace.normal.z == 1 ||
                            sFace.normal.z == -1
                        ) {
                            continue;
                        }
                        if (
                            isFrontFaceSnappingOtherProduct &&
                            !isFacingLeftRightSideOtherCabinet
                        ) {
                            getOtherBoxToClosestMesh(
                                otherBox,
                                otherProduct,
                                activeFace.leftRight,
                                Axis.Z,
                                isProductFillerProduct
                            );
                        }

                        if (isReturnProduct) {
                            const voidWidth =
                                ((otherProduct as CabinetMesh).userData.values
                                    .cabinet_depth *
                                    0.2 -
                                    (otherProduct as CabinetMesh).userData
                                        .values.void_width *
                                        0.2) *
                                2;

                            if (otherCabinetDirection == 'left') {
                                if (
                                    (otherProduct as CabinetMesh).userData
                                        .isRightReturnProduct
                                ) {
                                    otherBox.max.z -= voidWidth;
                                } else {
                                    otherBox.min.z += voidWidth;
                                }
                            } else {
                                if (
                                    (otherProduct as CabinetMesh).userData
                                        .isRightReturnProduct
                                ) {
                                    otherBox.min.z += voidWidth;
                                } else {
                                    otherBox.max.z -= voidWidth;
                                }
                            }
                        }

                        if (
                            isCabinetProductFiller &&
                            (isFacingLeftRightSideOtherCabinet ||
                                isFrontFaceSnappingOtherProduct)
                        ) {
                            bestDistance = zDist;
                            bestSnappedX =
                                targetPos.x + (oFace.position - sFace.position);
                            bestSnappedZ = resolveSnappedAlong(
                                'z',
                                shiftedBox,
                                otherBox,
                                targetPos
                            );
                        }

                        if (!isCabinetProductFiller) {
                            const zSnapResult = resolveCabinetSnapPosition({
                                axis: 'z',
                                selectedCenterX,
                                selectedCenterZ,
                                otherCenterX,
                                otherCenterZ,
                                selectedCabinetDirection,
                                isFrontFaceSnappingCabinetProduct,
                                isOtherProductFillerProduct,
                                isCabinetProductFiller,
                                selectedBoxWidth,
                                selectedBoxDepth,
                                otherBoxDepth,
                                shiftedBox,
                                otherBox,
                                cabinetGroup,
                                otherProduct: otherProduct as CabinetMesh,
                                delta,
                            });

                            shiftedBox = zSnapResult.shiftedBox;
                            bestSnappedZ = zSnapResult.snappedPrimary;

                            if (bestSnappedZ == null) {
                                continue;
                            }

                            bestDistance = zDist;

                            bestSnappedX = resolveSnappedAlong(
                                'x',
                                shiftedBox,
                                otherBox,
                                targetPos
                            );
                            bestSnappedZ -=
                                selectedCabinetDirection == 'normal'
                                    ? extraDepth
                                    : -extraDepth;
                        }
                    }

                    if (isSelectedCabinetLeftOrRight) {
                        if (
                            oFace.normal.z == sFace.normal.z ||
                            sFace.normal.z == 0
                        ) {
                            continue;
                        }

                        const LShapeException =
                            (isFrontFaceSnappingOtherProduct &&
                                !isLShapePanelSnappingLeftRightOther) ||
                            isOtherProductFillerProduct ||
                            isProductFillerProduct;

                        if (!LShapeException) {
                            getOtherBoxToClosestMesh(
                                otherBox,
                                otherProduct,
                                activeFace.frontBack,
                                Axis.X,
                                isProductFillerProduct
                            );
                        }
                        if (
                            (isFrontFaceSnappingCabinetProduct &&
                                isLShapePanelSnappingLeftRightCabient) ||
                            (isOtherProductFillerPanel &&
                                !isFrontFaceSnappingCabinetProduct)
                        ) {
                            const multiplier =
                                selectedCabinetDirection == 'right' ? 1 : -1;
                            shiftedBox = getShiftedBoxForClosetFace(
                                cabinetGroup,
                                new Vector3(0, 0, multiplier * 1),
                                delta
                            );
                        }

                        const SnappedZ = calculateBestSnapped({
                            isCabinetProductFiller,
                            oFace,
                            sFace,
                            activeFace,
                            targetPos,
                            otherCenterAlong: otherCenterZ,
                            selectedCenterAlong: selectedCenterZ,
                            selectedCabinetDirection,
                            otherBox,
                            selectedSizeAlong: selectedBoxDepth,
                            axis: 'z',
                            selectedCenterAcross: selectedCenterX,
                        });

                        // Handle the result
                        if (SnappedZ === null) {
                            continue;
                        }
                        bestSnappedZ = SnappedZ;
                        bestDistance = xDist;

                        bestSnappedX = resolveSnappedAlong(
                            'x',
                            shiftedBox,
                            otherBox,
                            targetPos
                        );
                    }
                }
            }
            snapState.snappedSide =
                oFace.normal.x !== 0
                    ? oFace.normal.x > 0
                        ? 'right'
                        : 'left'
                    : oFace.normal.z > 0
                      ? 'normal'
                      : 'back';
        }
    }
    if (bestSnappedX === null) return null;

    bestSnappedY = getSnappedY();

    if (!isCabinetProductFiller) {
        return new Vector3(
            bestSnappedX ?? targetPos.x,
            bestSnappedY,
            bestSnappedZ ?? targetPos.z
        );
    }

    if (
        isCabinetProductFiller &&
        (cabinetGroup as CabinetMesh).userData.values.cabinet_extend == 0
    ) {
        let minY = otherBox.min.y;
        if (legBox) {
            minY = legBox.max.y;
        }

        // Upper panel: top must reach otherBox.max.y, so boundsMaxY = otherBox.max.y always
        let boundsMaxY = isUpperProduct
            ? otherBox.max.y
            : selectedCabinetHeight > otherCabinetHeight
              ? minY + Number(selectedCabinetHeight)
              : otherBox.max.y;

        let boundsMinY = minY;
        boundsMinY = isOtherTallCabinetSnappableToUpper
            ? selectedBox.min.y
            : minY;
        boundsMinY =
            isOtherUpperProduct && !isUpperPanel ? selectedBox.min.y : minY;

        boundsMaxY = isOtherUpperProduct ? otherBox.max.y : boundsMaxY;
        snapState.bounds = [
            otherBox.max.z,
            boundsMaxY,
            otherBox.min.z,
            boundsMinY,
            otherBox.max.x,
            otherBox.min.x,
        ];
    } else {
        // Upper panel: top must reach otherBox.max.y, so boundsMaxY = otherBox.max.y always
        let boundsMaxY = isUpperProduct
            ? otherBox.max.y
            : selectedCabinetHeight > otherCabinetHeight
              ? otherBox.min.y + Number(selectedCabinetHeight)
              : otherBox.max.y;

        let boundsMinY = otherBox.min.y;
        boundsMinY =
            isOtherUpperProduct && !isUpperPanel
                ? selectedBox.min.y
                : otherBox.min.y;
        boundsMaxY =
            isOtherUpperProduct || isOtherTallCabinetSnappableToUpper
                ? otherBox.max.y
                : boundsMaxY;

        snapState.bounds = [
            otherBox.max.z,
            boundsMaxY,
            otherBox.min.z,
            boundsMinY,
            otherBox.max.x,
            otherBox.min.x,
        ];
    }
    snapState.mode = 'attached';

    return new Vector3(
        bestSnappedX ?? targetPos.x,
        bestSnappedY,
        bestSnappedZ ?? targetPos.z
    );
};

// Panels, fillers and products beside returns: snaps to the face of the
// other product towards the camera. A filler or panel whose snap would
// overlap another product rejects the pair, and one shorter than the product
// it snaps to is held in snap mode.
export const getFacingSnap: SnapStrategy = (
    cabinetGroup,
    otherProduct,
    ctx,
    pair
) => {
    const snapState: SnapState = {};
    const position = findFacingSnap(
        cabinetGroup,
        otherProduct,
        pair,
        ctx.targetPos,
        ctx.currentPos,
        getActiveFaceFromCamera(otherProduct, ctx.camera),
        snapState
    );
    if (position === null) return null;

    if (pair.selected.isFiller) {
        const wouldOverlap = checkCabinetOverlap(
            cabinetGroup as CabinetMesh,
            position,
            ctx.productsRef as CabinetMesh[],
            -0.1,
            true
        );
        if (wouldOverlap) {
            return {rejected: true, snapState: {mode: 'suspended'}};
        }

        if (pair.selected.height < pair.other.height) {
            snapState.mode = 'attached';
        }
    }

    return toCandidate(position, ctx.targetPos, snapState);
};
